import type {
  ProductStatusUpdateRequestType,
  ProductUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useProductStatusUpdate = (productId: number) => {
  const queryClient = useQueryClient();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useMutation({
    mutationFn: (data: ProductStatusUpdateRequestType) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `products/${productId}/status`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response).data as ProductUpdateResponseType,
      ),

    onSettled: (response) => {
      const queryKeys = [
        [business?.id, ServerStateKeys.Products, productId],
        [business?.id, ServerStateKeys.Products, productId, "supplier"],
        [user?.uid, business?.id, ServerStateKeys.Products],
        [user?.uid, business?.id, ServerStateKeys.StockOverview],
      ];
      if (response?.ParentId) {
        queryKeys.push(
          [business?.id, ServerStateKeys.Products, response.ParentId],
          [
            business?.id,
            ServerStateKeys.Products,
            response.ParentId,
            "supplier",
          ],
        );
      }
      return Promise.all(
        queryKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
};
